import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Alert,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ScienceIcon from '@mui/icons-material/Science';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BiotechIcon from '@mui/icons-material/Biotech';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import MedicationIcon from '@mui/icons-material/Medication';
import LabelIcon from '@mui/icons-material/Label';
import CheckIcon from '@mui/icons-material/Check';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import HealingIcon from '@mui/icons-material/Healing';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import OpacityIcon from '@mui/icons-material/Opacity';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import RadiologyIcon from '@mui/icons-material/MedicalServices';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import ElderlyIcon from '@mui/icons-material/Elderly';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { useTranslation } from 'react-i18next';

const CATEGORIES_MANAGEMENT_COPY = {
  de: {
    help: 'Kategorien helfen bei der thematischen Zuordnung und Filterung von Parametern',
    currentCategories: 'Aktuelle Kategorien',
    private: 'Privat',
    likeCategory: 'Kategorie liken',
    dislikeCategory: 'Kategorie disliken',
    addComment: 'Kommentar hinzufügen',
    removePrivateCategory: 'Private Kategorie entfernen',
    deleteCategory: 'Kategorie löschen',
    createdBy: 'Erstellt von {{user}}',
    unknown: 'Unbekannt',
    sendComment: 'Kommentar senden',
    noneAssigned: 'Keine Kategorien zugeordnet',
    proposeCategory: 'Neue Kategorie vorschlagen',
    pendingCategorySuggestions: 'Ausstehende Kategorie-Vorschläge',
    pendingHelp: 'Diese Kategorien warten noch auf Bestätigung durch Data Stewards oder die Community',
    editCategory: 'Kategorie bearbeiten',
    categoryName: 'Kategoriename',
    iconSelected: 'Icon ausgewählt',
    selectIconOptional: 'Icon auswählen (optional)',
    save: 'Speichern',
    cancel: 'Abbrechen',
    suggestLike: 'Vorschlag liken',
    suggestDislike: 'Vorschlag disliken',
    editOwnSuggestion: 'Eigenen Vorschlag bearbeiten',
    deleteOwnSuggestion: 'Eigenen Vorschlag löschen',
    suggestedBy: 'Vorgeschlagen von {{user}}',
    approve: 'Genehmigen',
    reject: 'Ablehnen',
    addCategoryTitle: 'Neue Kategorie vorschlagen',
    categoryNameRequired: 'Kategoriename*',
    categoryPlaceholder: 'z.B. Beatmung, Dialyse...',
    icon: 'Icon (optional)',
    selectIcon: 'Icon auswählen',
    createPrivateCategory: 'Als private Kategorie erstellen',
    privateInfo: 'Private Kategorien sind nur für Sie sichtbar und benötigen keine Validierung.',
    publicInfo: 'Öffentliche Kategorien werden zur Prüfung an das Data Steward Team gesendet. Bei ausreichender Community-Unterstützung können sie automatisch genehmigt werden.',
    suggest: 'Vorschlagen',
    iconDialogTitle: 'Icon auswählen',
    noIcon: 'Kein Icon',
    publish: 'Veröffentlichen',
    deleteTitle: 'Kategorie löschen?',
    deletePrompt: 'Möchten Sie diese Kategorie wirklich löschen?',
    createdByLabel: 'Erstellt von: {{user}}',
    cannotUndo: 'Diese Aktion kann nicht rückgängig gemacht werden.',
  },
  en: {
    help: 'Categories help classify and filter parameters thematically',
    currentCategories: 'Current categories',
    private: 'Private',
    likeCategory: 'Like category',
    dislikeCategory: 'Dislike category',
    addComment: 'Add comment',
    removePrivateCategory: 'Remove private category',
    deleteCategory: 'Delete category',
    createdBy: 'Created by {{user}}',
    unknown: 'Unknown',
    sendComment: 'Send comment',
    noneAssigned: 'No categories assigned',
    proposeCategory: 'Suggest new category',
    pendingCategorySuggestions: 'Pending category suggestions',
    pendingHelp: 'These categories are still waiting for confirmation by data stewards or the community',
    editCategory: 'Edit category',
    categoryName: 'Category name',
    iconSelected: 'Icon selected',
    selectIconOptional: 'Select icon (optional)',
    save: 'Save',
    cancel: 'Cancel',
    suggestLike: 'Like suggestion',
    suggestDislike: 'Dislike suggestion',
    editOwnSuggestion: 'Edit own suggestion',
    deleteOwnSuggestion: 'Delete own suggestion',
    suggestedBy: 'Suggested by {{user}}',
    approve: 'Approve',
    reject: 'Reject',
    addCategoryTitle: 'Suggest new category',
    categoryNameRequired: 'Category name*',
    categoryPlaceholder: 'e.g. Ventilation, Dialysis...',
    icon: 'Icon (optional)',
    selectIcon: 'Select icon',
    createPrivateCategory: 'Create as private category',
    privateInfo: 'Private categories are only visible to you and do not require validation.',
    publicInfo: 'Public categories are sent to the data steward team for review. With enough community support, they can be approved automatically.',
    suggest: 'Submit suggestion',
    iconDialogTitle: 'Select icon',
    noIcon: 'No icon',
    publish: 'Publish',
    deleteTitle: 'Delete category?',
    deletePrompt: 'Do you really want to delete this category?',
    createdByLabel: 'Created by: {{user}}',
    cannotUndo: 'This action cannot be undone.',
  },
  pt: {
    help: 'As categorias ajudam na classificação temática e filtragem dos parâmetros',
    currentCategories: 'Categorias atuais',
    private: 'Privada',
    likeCategory: 'Gostar da categoria',
    dislikeCategory: 'Não gostar da categoria',
    addComment: 'Adicionar comentário',
    removePrivateCategory: 'Remover categoria privada',
    deleteCategory: 'Eliminar categoria',
    createdBy: 'Criado por {{user}}',
    unknown: 'Desconhecido',
    sendComment: 'Enviar comentário',
    noneAssigned: 'Nenhuma categoria atribuída',
    proposeCategory: 'Sugerir nova categoria',
    pendingCategorySuggestions: 'Sugestões de categoria pendentes',
    pendingHelp: 'Estas categorias ainda aguardam confirmação pelos data stewards ou pela comunidade',
    editCategory: 'Editar categoria',
    categoryName: 'Nome da categoria',
    iconSelected: 'Ícone selecionado',
    selectIconOptional: 'Selecionar ícone (opcional)',
    save: 'Guardar',
    cancel: 'Cancelar',
    suggestLike: 'Gostar da sugestão',
    suggestDislike: 'Não gostar da sugestão',
    editOwnSuggestion: 'Editar a própria sugestão',
    deleteOwnSuggestion: 'Eliminar a própria sugestão',
    suggestedBy: 'Sugerido por {{user}}',
    approve: 'Aprovar',
    reject: 'Rejeitar',
    addCategoryTitle: 'Sugerir nova categoria',
    categoryNameRequired: 'Nome da categoria*',
    categoryPlaceholder: 'ex.: Ventilação, Diálise...',
    icon: 'Ícone (opcional)',
    selectIcon: 'Selecionar ícone',
    createPrivateCategory: 'Criar como categoria privada',
    privateInfo: 'As categorias privadas são visíveis apenas para si e não requerem validação.',
    publicInfo: 'As categorias públicas são enviadas para revisão pela equipa de data stewards. Com apoio suficiente da comunidade, podem ser aprovadas automaticamente.',
    suggest: 'Enviar sugestão',
    iconDialogTitle: 'Selecionar ícone',
    noIcon: 'Sem ícone',
    publish: 'Publicar',
    deleteTitle: 'Eliminar categoria?',
    deletePrompt: 'Pretende realmente eliminar esta categoria?',
    createdByLabel: 'Criado por: {{user}}',
    cannotUndo: 'Esta ação não pode ser anulada.',
  },
};

/**
 * CategoriesManagement Component
 * Manages category tags for parameters with icon selection and voting
 *
 * @param {object} props
 * @param {array} props.tags - Current tags/categories
 * @param {array} props.availableTags - All available tags
 * @param {array} props.tagDetails - Structured tag entries from the backend
 * @param {function} props.onAddTag - Callback when tag is added
 * @param {function} props.onApproveTag - Callback when pending tag is approved
 * @param {function} props.onRejectTag - Callback when pending tag is rejected
 * @param {function} props.onVoteTag - Callback when a tag is voted on
 * @param {function} props.onCommentTag - Callback when a tag receives a comment
 * @param {function} props.onRemoveTag - Callback when tag is removed
 * @param {string} props.currentUser - Current user name
 * @param {string} props.userRole - User role (user, data_steward, admin)
 */
const CategoriesManagement = ({
  tags = [],
  availableTags = [],
  tagDetails = [],
  onAddTag,
  onApproveTag,
  onRejectTag,
  onVoteTag,
  onCommentTag,
  onRemoveTag,
  currentUser = 'Current User',
  userRole = 'user'
}) => {
  const { i18n } = useTranslation();
  const [selectedTagIcon, setSelectedTagIcon] = useState(null);
  const [createTagPrivate, setCreateTagPrivate] = useState(false);
  const [showIconDialog, setShowIconDialog] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);

  // Local state for newly created tags
  const [localTags, setLocalTags] = useState([]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuTag, setContextMenuTag] = useState(null);

  // Comment state
  const [commentText, setCommentText] = useState({});
  const [showCommentInput, setShowCommentInput] = useState({});

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Edit state for own category suggestions
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryData, setEditCategoryData] = useState({ name: '', icon: null });

  // State for current categories likes/dislikes - loaded from JSON
  const [currentCategoriesStats, setCurrentCategoriesStats] = useState({});

  // Local state for pending category suggestions
  const [pendingCategorySuggestions, setPendingCategorySuggestions] = useState([]);
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = CATEGORIES_MANAGEMENT_COPY[language];
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';

  useEffect(() => {
    const normalizedTagDetails = (Array.isArray(tagDetails) ? tagDetails : [])
      .filter(tag => tag && typeof tag === 'object' && tag.name)
      .map(tag => ({
        id: tag.id ?? null,
        name: tag.name,
        createdBy: tag.suggestedBy || tag.createdBy || copy.unknown,
        isPrivate: Boolean(tag.isPrivate),
        icon: tag.icon || null,
        validated: Boolean(tag.validated ?? tag.status === 'approved'),
        status: tag.status || (tag.validated ? 'approved' : 'pending_review'),
        likes: tag.likes || 0,
        dislikes: tag.dislikes || 0,
        likedBy: tag.likedBy || [],
        dislikedBy: tag.dislikedBy || [],
        comments: tag.comments || [],
      }));

    const visibleTags = normalizedTagDetails.filter(tag => tag.validated || tag.isPrivate);
    const pendingTags = normalizedTagDetails.filter(tag => !tag.validated && !tag.isPrivate);

    setLocalTags(visibleTags);
    setPendingCategorySuggestions(pendingTags);
    setCurrentCategoriesStats(
      visibleTags.reduce((acc, tag) => {
        acc[tag.name] = {
          likes: tag.likes || 0,
          dislikes: tag.dislikes || 0,
          likedBy: tag.likedBy || [],
          dislikedBy: tag.dislikedBy || [],
          comments: tag.comments || [],
        };
        return acc;
      }, {})
    );
  }, [tagDetails, copy.unknown]);

  // Expanded icon options for tags
  const tagIconOptions = [
    { name: 'monitor_heart', icon: MonitorHeartIcon, label: 'Vitalparameter', category: 'Vital' },
    { name: 'bloodtype', icon: BloodtypeIcon, label: 'Blut', category: 'Vital' },
    { name: 'favorite', icon: FavoriteBorderIcon, label: 'Herz', category: 'Vital' },
    { name: 'thermostat', icon: ThermostatIcon, label: 'Temperatur', category: 'Vital' },
    { name: 'opacity', icon: OpacityIcon, label: 'Flüssigkeit', category: 'Vital' },

    { name: 'science', icon: ScienceIcon, label: 'Labor', category: 'Labor' },
    { name: 'biotech', icon: BiotechIcon, label: 'Forschung', category: 'Labor' },
    { name: 'coronavirus', icon: CoronavirusIcon, label: 'Mikrobiologie', category: 'Labor' },

    { name: 'local_hospital', icon: LocalHospitalIcon, label: 'Klinik', category: 'Behandlung' },
    { name: 'vaccines', icon: VaccinesIcon, label: 'Impfung', category: 'Behandlung' },
    { name: 'medication', icon: MedicationIcon, label: 'Medikation', category: 'Behandlung' },
    { name: 'healing', icon: HealingIcon, label: 'Wundversorgung', category: 'Behandlung' },
    { name: 'local_pharmacy', icon: LocalPharmacyIcon, label: 'Pharmazie', category: 'Behandlung' },
    { name: 'radiology', icon: RadiologyIcon, label: 'Radiologie', category: 'Behandlung' },

    { name: 'psychology', icon: PsychologyIcon, label: 'Neurologie', category: 'Spezialgebiet' },
    { name: 'accessibility', icon: AccessibilityNewIcon, label: 'Rehabilitation', category: 'Spezialgebiet' },
    { name: 'pregnant_woman', icon: PregnantWomanIcon, label: 'Geburtshilfe', category: 'Spezialgebiet' },
    { name: 'child_care', icon: ChildCareIcon, label: 'Pädiatrie', category: 'Spezialgebiet' },
    { name: 'elderly', icon: ElderlyIcon, label: 'Geriatrie', category: 'Spezialgebiet' },
    { name: 'fitness_center', icon: FitnessCenterIcon, label: 'Physiotherapie', category: 'Spezialgebiet' }
  ];

  // Mock tag data with statistics and validation status - loaded from JSON
  // Merge with local tags
  // Only validated categories or private categories appear here
  const allTagsWithStats = localTags.filter(t => t.validated || t.isPrivate);
  const propTagNames = tags
    .map(tag => (typeof tag === 'string' ? tag : tag?.name))
    .filter(Boolean);

  // Combined tags from props, local state, and pending suggestions
  // Used ONLY for duplicate checking when adding new tags
  const allExistingTagNames = [
    ...propTagNames,
    ...localTags.map(t => t.name),
    ...pendingCategorySuggestions.map(s => s.name)
  ];

  const getIconComponent = (iconName) => {
    const iconOption = tagIconOptions.find(opt => opt.name === iconName);
    return iconOption?.icon || null;
  };

  const handleAddTag = () => {
    if (newTagName.trim() && !allExistingTagNames.includes(newTagName.trim())) {
      if (createTagPrivate) {
        // Private tags go directly to "Aktuelle Kategorien" (auto-validated)
        const newTag = {
          name: newTagName.trim(),
          createdBy: currentUser,
          isPrivate: true,
          icon: selectedTagIcon,
          validated: true
        };
        setLocalTags([...localTags, newTag]);

        // Call parent callback
        if (onAddTag) {
          onAddTag(newTagName.trim(), true, selectedTagIcon);
        }
      } else {
        // Public tags go to "Ausstehende Kategorie-Vorschläge" (pending validation)
        const newSuggestion = {
          id: Date.now(),
          name: newTagName.trim(),
          icon: selectedTagIcon,
          isPrivate: false,
          suggestedBy: currentUser,
          suggestedDate: new Date().toISOString().split('T')[0],
          likes: 0,
          dislikes: 0,
          likedBy: [],
          dislikedBy: [],
          comments: []
        };
        setPendingCategorySuggestions([...pendingCategorySuggestions, newSuggestion]);

        // Call parent callback to update header card
        if (onAddTag) {
          onAddTag(newTagName.trim(), false, selectedTagIcon);
        }
      }

      // Reset and close dialog
      setNewTagName('');
      setSelectedTagIcon(null);
      setCreateTagPrivate(false);
      setShowAddCategoryDialog(false);
    }
  };

  const handleDeleteCategoryClick = (tagName, tagData) => {
    setCategoryToDelete({ name: tagName, data: tagData });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      const tagName = categoryToDelete.name;
      // Remove from local tags if it exists there
      const localTag = localTags.find(t => t.name === tagName);
      if (localTag) {
        setLocalTags(localTags.filter(t => t.name !== tagName));
      }

      if (onRemoveTag) {
        onRemoveTag(tagName);
      }
    }
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleRemoveTag = (tagName) => {
    // Remove from local tags if it exists there
    const localTag = localTags.find(t => t.name === tagName);
    if (localTag) {
      setLocalTags(localTags.filter(t => t.name !== tagName));
    }

    if (onRemoveTag) {
      onRemoveTag(tagName);
    }
  };

  const handleSelectIcon = (iconName) => {
    setSelectedTagIcon(iconName);
    setShowIconDialog(false);
  };

  const handleApproveCategorySuggestion = async (suggestionId) => {
    const suggestion = pendingCategorySuggestions.find(s => s.id === suggestionId);
    if (suggestion) {
      if (onApproveTag) {
        onApproveTag(suggestion.name, suggestion.icon);
        return;
      }

      const approvedTag = {
        ...suggestion,
        createdBy: suggestion.suggestedBy,
        validated: true,
        status: 'approved',
      };
      setLocalTags([...localTags, approvedTag]);
      setPendingCategorySuggestions(pendingCategorySuggestions.filter(s => s.id !== suggestionId));
    }
  };

  const handleRejectCategorySuggestion = async (suggestionId) => {
    const suggestion = pendingCategorySuggestions.find(s => s.id === suggestionId);
    if (suggestion) {
      if (onRejectTag) {
        onRejectTag(suggestion.id);
        return;
      }

      setPendingCategorySuggestions(pendingCategorySuggestions.filter(s => s.id !== suggestionId));
      if (onRemoveTag) {
        onRemoveTag(suggestion.name);
      }
    }
  };

  const handleLikeCategory = async (suggestionId) => {
    if (onVoteTag) {
      onVoteTag(suggestionId, 'like');
      return;
    }
    setPendingCategorySuggestions(
      pendingCategorySuggestions.map(s => {
        if (s.id === suggestionId) {
          // Remove from disliked if previously disliked
          const wasDisliked = s.dislikedBy.includes(currentUser);
          const newDislikedBy = wasDisliked
            ? s.dislikedBy.filter(u => u !== currentUser)
            : s.dislikedBy;
          const newDislikes = wasDisliked ? s.dislikes - 1 : s.dislikes;

          // Add to liked
          return {
            ...s,
            likes: s.likes + 1,
            likedBy: [...s.likedBy, currentUser],
            dislikes: newDislikes,
            dislikedBy: newDislikedBy
          };
        }
        return s;
      })
    );
  };

  const handleDislikeCategory = async (suggestionId) => {
    if (onVoteTag) {
      onVoteTag(suggestionId, 'dislike');
      return;
    }
    setPendingCategorySuggestions(
      pendingCategorySuggestions.map(s => {
        if (s.id === suggestionId) {
          // Remove from liked if previously liked
          const wasLiked = s.likedBy.includes(currentUser);
          const newLikedBy = wasLiked
            ? s.likedBy.filter(u => u !== currentUser)
            : s.likedBy;
          const newLikes = wasLiked ? s.likes - 1 : s.likes;

          // Add to disliked
          return {
            ...s,
            dislikes: s.dislikes + 1,
            dislikedBy: [...s.dislikedBy, currentUser],
            likes: newLikes,
            likedBy: newLikedBy
          };
        }
        return s;
      })
    );
  };

  const handleAddCategoryComment = async (suggestionId) => {
    const comment = commentText[suggestionId];
    if (comment && comment.trim()) {
      if (onCommentTag) {
        onCommentTag(suggestionId, comment.trim());
        setCommentText({ ...commentText, [suggestionId]: '' });
        setShowCommentInput({ ...showCommentInput, [suggestionId]: false });
        return;
      }
      setPendingCategorySuggestions(
        pendingCategorySuggestions.map(s =>
          s.id === suggestionId
            ? {
                ...s,
                comments: [
                  ...s.comments,
                  {
                    author: currentUser,
                    text: comment.trim(),
                    date: new Date().toISOString().split('T')[0]
                  }
                ]
              }
            : s
        )
      );
      setCommentText({ ...commentText, [suggestionId]: '' });
      setShowCommentInput({ ...showCommentInput, [suggestionId]: false });
    }
  };

  // Handlers for current categories
  const handleLikeCurrentCategory = async (tagEntry) => {
    if (onVoteTag && tagEntry?.id) {
      onVoteTag(tagEntry.id, 'like');
      return;
    }

    const categoryName = tagEntry?.name || '';
    setCurrentCategoriesStats(prevStats => {
      const stats = prevStats[categoryName] || { likes: 0, dislikes: 0, likedBy: [], dislikedBy: [], comments: [] };
      const wasDisliked = stats.dislikedBy.includes(currentUser);
      const newDislikedBy = wasDisliked
        ? stats.dislikedBy.filter(u => u !== currentUser)
        : stats.dislikedBy;
      const newDislikes = wasDisliked ? stats.dislikes - 1 : stats.dislikes;

      return {
        ...prevStats,
        [categoryName]: {
          ...stats,
          likes: stats.likes + 1,
          likedBy: [...stats.likedBy, currentUser],
          dislikes: newDislikes,
          dislikedBy: newDislikedBy
        }
      };
    });
  };

  const handleDislikeCurrentCategory = async (tagEntry) => {
    if (onVoteTag && tagEntry?.id) {
      onVoteTag(tagEntry.id, 'dislike');
      return;
    }

    const categoryName = tagEntry?.name || '';
    setCurrentCategoriesStats(prevStats => {
      const stats = prevStats[categoryName] || { likes: 0, dislikes: 0, likedBy: [], dislikedBy: [], comments: [] };
      const wasLiked = stats.likedBy.includes(currentUser);
      const newLikedBy = wasLiked
        ? stats.likedBy.filter(u => u !== currentUser)
        : stats.likedBy;
      const newLikes = wasLiked ? stats.likes - 1 : stats.likes;

      return {
        ...prevStats,
        [categoryName]: {
          ...stats,
          dislikes: stats.dislikes + 1,
          dislikedBy: [...stats.dislikedBy, currentUser],
          likes: newLikes,
          likedBy: newLikedBy
        }
      };
    });
  };

  const handleAddCurrentCategoryComment = async (tagEntry) => {
    const categoryName = tagEntry?.name || '';
    const comment = commentText[`current_${categoryName}`];
    if (comment && comment.trim()) {
      if (onCommentTag && tagEntry?.id) {
        onCommentTag(tagEntry.id, comment.trim());
        setCommentText({ ...commentText, [`current_${categoryName}`]: '' });
        setShowCommentInput({ ...showCommentInput, [`current_${categoryName}`]: false });
        return;
      }
      setCurrentCategoriesStats(prevStats => {
        const stats = prevStats[categoryName] || { likes: 0, dislikes: 0, likedBy: [], dislikedBy: [], comments: [] };
        return {
          ...prevStats,
          [categoryName]: {
            ...stats,
            comments: [
              ...stats.comments,
              {
                author: currentUser,
                text: comment.trim(),
                date: new Date().toISOString().split('T')[0]
              }
            ]
          }
        };
      });
      setCommentText({ ...commentText, [`current_${categoryName}`]: '' });
      setShowCommentInput({ ...showCommentInput, [`current_${categoryName}`]: false });
    }
  };

  const handleContextMenu = (event, tagName, tagData) => {
    event.preventDefault();
    // Only show context menu for private categories owned by current user
    if (tagData?.isPrivate && tagData?.createdBy === currentUser) {
      setContextMenu({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
      });
      setContextMenuTag(tagName);
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setContextMenuTag(null);
  };

  const handlePublishPrivateCategory = () => {
    if (contextMenuTag) {
      // Convert private category to public
      setLocalTags(localTags.map(t =>
        t.name === contextMenuTag
          ? { ...t, isPrivate: false, validated: false } // Now needs validation
          : t
      ));
    }
    handleCloseContextMenu();
  };

  // Edit handlers for own category suggestions
  const handleEditCategorySuggestion = (suggestion) => {
    setEditingCategoryId(suggestion.id);
    setEditCategoryData({
      name: suggestion.name,
      icon: suggestion.icon
    });
  };

  const handleSaveEditCategory = () => {
    if (editCategoryData.name.trim()) {
      setPendingCategorySuggestions(
        pendingCategorySuggestions.map(s =>
          s.id === editingCategoryId
            ? {
                ...s,
                name: editCategoryData.name.trim(),
                icon: editCategoryData.icon
              }
            : s
        )
      );
      setEditingCategoryId(null);
      setEditCategoryData({ name: '', icon: null });
    }
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditCategoryData({ name: '', icon: null });
  };

  // Group icons by category for better organization
  const iconCategories = tagIconOptions.reduce((acc, iconOpt) => {
    if (!acc[iconOpt.category]) {
      acc[iconOpt.category] = [];
    }
    acc[iconOpt.category].push(iconOpt);
    return acc;
  }, {});

  // Pastel colors for validated, pending, and private categories
  const getChipColor = (isValidated, isPrivate) => {
    if (isPrivate) return { bg: '#e3f2fd', border: '#42a5f5', color: '#1565c0' }; // Blue for private
    if (isValidated) return { bg: '#e8f5e9', border: '#66bb6a', color: '#2e7d32' };
    return { bg: '#fff3e0', border: '#ffb74d', color: '#f57c00' };
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
        {copy.help}
      </Typography>

      {/* Current Tags - with scrolling */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {copy.currentCategories}
        </Typography>
        <Box
          sx={{
            maxHeight: '350px',
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#555',
              }
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {allTagsWithStats.length > 0 ? (
            allTagsWithStats.map(tagData => {
              const tag = tagData.name;
              const isPrivate = tagData?.isPrivate || false;
              const isOwnTag = tagData?.createdBy === currentUser;
              const isValidated = tagData?.validated || false;
              const CustomIconComponent = tagData?.icon ? getIconComponent(tagData.icon) : null;
              const chipColors = getChipColor(isValidated, isPrivate);

              // Get stats for this category
              const categoryStats = currentCategoriesStats[tag] || { likes: 0, dislikes: 0, likedBy: [], dislikedBy: [], comments: [] };

              return (
                <Paper
                  key={tag}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    backgroundColor: chipColors.bg,
                    borderColor: chipColors.border
                  }}
                  onContextMenu={(e) => handleContextMenu(e, tag, tagData)}
                >
                  {/* Header row with category name, icon, status badges, and like/dislike */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {CustomIconComponent && <CustomIconComponent fontSize="small" sx={{ color: chipColors.color }} />}
                      <Typography variant="body2" fontWeight="bold" sx={{ color: chipColors.color }}>
                        {tag}
                      </Typography>
                      {isPrivate && (
                        <Chip
                          icon={<LockIcon fontSize="small" />}
                          label={copy.private}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                      {isValidated && (
                        <CheckIcon color="success" fontSize="small" />
                      )}
                    </Box>

                    {/* Like/Dislike/Comment buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleLikeCurrentCategory(tagData)}
                          disabled={isOwnTag || categoryStats.likedBy.includes(currentUser)}
                          aria-label={`${copy.likeCategory}: ${tag}`}
                          title={copy.likeCategory}
                          sx={{
                            p: 0.3,
                            color: categoryStats.likedBy.includes(currentUser) ? '#4caf50' : 'inherit'
                          }}
                        >
                          <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                          {categoryStats.likes}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDislikeCurrentCategory(tagData)}
                          disabled={isOwnTag || categoryStats.dislikedBy.includes(currentUser)}
                          aria-label={`${copy.dislikeCategory}: ${tag}`}
                          title={copy.dislikeCategory}
                          sx={{
                            p: 0.3,
                            color: categoryStats.dislikedBy.includes(currentUser) ? '#f44336' : 'inherit'
                          }}
                        >
                          <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                          {categoryStats.dislikes}
                        </Typography>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() =>
                          setShowCommentInput({
                            ...showCommentInput,
                            [`current_${tag}`]: !showCommentInput[`current_${tag}`]
                          })
                        }
                        aria-label={`${copy.addComment}: ${tag}`}
                        title={copy.addComment}
                        sx={{ p: 0.3 }}
                      >
                        <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                      </IconButton>

                      {/* Delete button - for Data Steward/Admin or private category owner */}
                      {((userRole === 'data_steward' || userRole === 'admin') || (isPrivate && isOwnTag)) && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Private category owners can delete directly, Data Stewards need confirmation
                            if (isPrivate && isOwnTag) {
                              handleRemoveTag(tag);
                            } else {
                              handleDeleteCategoryClick(tag, tagData);
                            }
                          }}
                          aria-label={`${copy.deleteCategory}: ${tag}`}
                          title={isPrivate && isOwnTag ? copy.removePrivateCategory : copy.deleteCategory}
                          sx={{ p: 0.3 }}
                        >
                          {isPrivate && isOwnTag ? <CloseIcon fontSize="small" sx={{ fontSize: '0.9rem' }} /> : <DeleteOutlineIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />}
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Category metadata */}
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                    {copy.createdBy.replace('{{user}}', tagData?.createdBy || copy.unknown)}
                  </Typography>

                  {/* Comment input (collapsible) */}
                  {showCommentInput[`current_${tag}`] && (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={copy.addComment}
                        value={commentText[`current_${tag}`] || ''}
                        onChange={e =>
                          setCommentText({ ...commentText, [`current_${tag}`]: e.target.value })
                        }
                        sx={{ mb: 0.5 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAddCurrentCategoryComment(tagData)}
                        disabled={!commentText[`current_${tag}`]?.trim()}
                        sx={{ py: 0.3 }}
                      >
                        {copy.sendComment}
                      </Button>
                    </Box>
                  )}

                  {/* Display existing comments */}
                  {categoryStats.comments && categoryStats.comments.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                      {categoryStats.comments.map((comment, idx) => (
                        <Box key={idx} sx={{ mb: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <Typography variant="caption" fontWeight="bold">
                              {comment.author}:
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                              {new Date(comment.date).toLocaleDateString(locale)}
                            </Typography>
                          </Box>
                          <Typography variant="caption">
                            {comment.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              );
            })
          ) : (
            <Typography variant="caption" color="textSecondary">
              {copy.noneAssigned}
            </Typography>
          )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Add New Tag - Button to open dialog */}
      <Box>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<AddIcon />}
          onClick={() => setShowAddCategoryDialog(true)}
        >
          {copy.proposeCategory}
        </Button>

        {/* Pending Category Suggestions */}
        {pendingCategorySuggestions.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <InfoOutlinedIcon color="warning" />
              <Typography variant="subtitle2" fontWeight="bold">
                {copy.pendingCategorySuggestions}
              </Typography>
              <Chip
                label={pendingCategorySuggestions.length}
                size="small"
                color="warning"
              />
            </Box>

            <Typography variant="caption" color="textSecondary" gutterBottom display="block" sx={{ mb: 2 }}>
              {copy.pendingHelp}
            </Typography>

            {/* Pending Category Suggestions from others */}
            {pendingCategorySuggestions.map(suggestion => {
              const SuggestionIconComponent = suggestion.icon ? getIconComponent(suggestion.icon) : null;
              const isOwnSuggestion = suggestion.suggestedBy === currentUser;
              return (
                <Paper key={suggestion.id} variant="outlined" sx={{ p: 1.5, backgroundColor: '#fff9e6', borderColor: '#ff9800', mb: 1.5 }}>
                  {editingCategoryId === suggestion.id ? (
                    /* Edit Mode */
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {copy.editCategory}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label={copy.categoryName}
                        value={editCategoryData.name}
                        onChange={(e) => setEditCategoryData({ ...editCategoryData, name: e.target.value })}
                        sx={{ mb: 1.5 }}
                      />
                      <Button
                        variant="outlined"
                        startIcon={editCategoryData.icon ? null : <AddIcon />}
                        onClick={() => setShowIconDialog(true)}
                        sx={{ width: '100%', justifyContent: 'flex-start', mb: 1.5 }}
                      >
                        {editCategoryData.icon ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {React.createElement(getIconComponent(editCategoryData.icon))}
                            <Typography variant="body2">
                              {tagIconOptions.find(opt => opt.name === editCategoryData.icon)?.label || copy.iconSelected}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditCategoryData({ ...editCategoryData, icon: null });
                              }}
                              sx={{ ml: 'auto' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          copy.selectIconOptional
                        )}
                      </Button>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSaveEditCategory}
                          disabled={!editCategoryData.name.trim()}
                          sx={{ flex: 1 }}
                        >
                          {copy.save}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleCancelEditCategory}
                          sx={{ flex: 1 }}
                        >
                          {copy.cancel}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    /* Normal View */
                    <>
                      {/* Header with name and like/dislike/comment/edit/delete buttons */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {SuggestionIconComponent && <SuggestionIconComponent fontSize="small" />}
                          <Typography variant="body2" fontWeight="bold">
                            {suggestion.name}
                          </Typography>
                          {suggestion.isPrivate && (
                            <Chip label={copy.private} size="small" icon={<LockIcon fontSize="small" />} sx={{ height: 20 }} />
                          )}
                        </Box>

                        {/* Like/Dislike/Comment/Edit/Delete buttons on the right */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleLikeCategory(suggestion.id)}
                              disabled={isOwnSuggestion || suggestion.likedBy.includes(currentUser)}
                              aria-label={`${copy.suggestLike}: ${suggestion.name}`}
                              title={copy.suggestLike}
                              sx={{
                                p: 0.3,
                                color: suggestion.likedBy.includes(currentUser) ? '#4caf50' : 'inherit'
                              }}
                            >
                              <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                            <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                              {suggestion.likes}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleDislikeCategory(suggestion.id)}
                              disabled={isOwnSuggestion || suggestion.dislikedBy.includes(currentUser)}
                              aria-label={`${copy.suggestDislike}: ${suggestion.name}`}
                              title={copy.suggestDislike}
                              sx={{
                                p: 0.3,
                                color: suggestion.dislikedBy.includes(currentUser) ? '#f44336' : 'inherit'
                              }}
                            >
                              <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                            <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                              {suggestion.dislikes}
                            </Typography>
                          </Box>

                          {/* Comment button */}
                          <IconButton
                            size="small"
                            onClick={() =>
                              setShowCommentInput({
                                ...showCommentInput,
                                [suggestion.id]: !showCommentInput[suggestion.id]
                              })
                            }
                            aria-label={`${copy.addComment}: ${suggestion.name}`}
                            title={copy.addComment}
                            sx={{ p: 0.3 }}
                          >
                            <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>

                          {/* Edit button - only for the creator */}
                          {isOwnSuggestion && (
                            <IconButton
                              size="small"
                              onClick={() => handleEditCategorySuggestion(suggestion)}
                              aria-label={`${copy.editOwnSuggestion}: ${suggestion.name}`}
                              title={copy.editOwnSuggestion}
                              sx={{ p: 0.3 }}
                            >
                              <EditIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          )}

                          {/* Delete button - only for the creator */}
                          {isOwnSuggestion && (
                            <IconButton
                              size="small"
                              onClick={() => handleRejectCategorySuggestion(suggestion.id)}
                              aria-label={`${copy.deleteOwnSuggestion}: ${suggestion.name}`}
                              title={copy.deleteOwnSuggestion}
                              sx={{
                                p: 0.3,
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                }
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      {/* Suggested by info */}
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: suggestion.comments?.length > 0 || showCommentInput[suggestion.id] ? 1 : 0 }}>
                        {copy.suggestedBy.replace('{{user}}', suggestion.suggestedBy)}
                      </Typography>
                    </>
                  )}

                  {/* Action buttons - only for Data Stewards/Admins */}
                  {(userRole === 'data_steward' || userRole === 'admin') && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<CheckIcon />}
                        onClick={() => handleApproveCategorySuggestion(suggestion.id)}
                        sx={{ flex: 1, py: 0.3 }}
                      >
                        {copy.approve}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CloseIcon />}
                        onClick={() => handleRejectCategorySuggestion(suggestion.id)}
                        sx={{ flex: 1, py: 0.3 }}
                      >
                        {copy.reject}
                      </Button>
                    </Box>
                  )}

                  {/* Comment input (collapsible) */}
                  {showCommentInput[suggestion.id] && (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={copy.addComment}
                        value={commentText[suggestion.id] || ''}
                        onChange={e =>
                          setCommentText({ ...commentText, [suggestion.id]: e.target.value })
                        }
                        sx={{ mb: 0.5 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAddCategoryComment(suggestion.id)}
                        disabled={!commentText[suggestion.id]?.trim()}
                        sx={{ py: 0.3 }}
                      >
                        {copy.sendComment}
                      </Button>
                    </Box>
                  )}

                  {/* Display existing comments */}
                  {suggestion.comments && suggestion.comments.length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                      {suggestion.comments.map((comment, idx) => (
                        <Box key={idx} sx={{ mb: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <Typography variant="caption" fontWeight="bold">
                              {comment.author}:
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                              {new Date(comment.date).toLocaleDateString(locale)}
                            </Typography>
                          </Box>
                          <Typography variant="caption">
                            {comment.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Add Category Dialog */}
      <Dialog
        open={showAddCategoryDialog}
        onClose={() => {
          setShowAddCategoryDialog(false);
          setNewTagName('');
          setSelectedTagIcon(null);
          setCreateTagPrivate(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {copy.addCategoryTitle}
          <IconButton
            onClick={() => {
              setShowAddCategoryDialog(false);
              setNewTagName('');
              setSelectedTagIcon(null);
              setCreateTagPrivate(false);
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Category Name Input */}
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {copy.categoryNameRequired}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={copy.categoryPlaceholder}
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              sx={{ mb: 3 }}
              autoFocus
              InputProps={{
                startAdornment: selectedTagIcon && (
                  <InputAdornment position="start">
                    {React.createElement(getIconComponent(selectedTagIcon), { fontSize: 'small' })}
                  </InputAdornment>
                ),
              }}
            />

            {/* Icon Selection */}
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {copy.icon}
            </Typography>
            <Button
              variant="outlined"
              startIcon={selectedTagIcon ? null : <AddIcon />}
              onClick={() => setShowIconDialog(true)}
              sx={{ width: '100%', justifyContent: 'flex-start', mb: 3 }}
            >
              {selectedTagIcon ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {React.createElement(getIconComponent(selectedTagIcon))}
                  <Typography variant="body2">
                    {tagIconOptions.find(opt => opt.name === selectedTagIcon)?.label || copy.iconSelected}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTagIcon(null);
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                copy.selectIcon
              )}
            </Button>

            {/* Privacy Toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={createTagPrivate}
                  onChange={(e) => setCreateTagPrivate(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon fontSize="small" />
                  <Typography variant="body2">
                    {copy.createPrivateCategory}
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />

            <Alert severity="info">
              <Typography variant="caption">
                {createTagPrivate
                  ? copy.privateInfo
                  : copy.publicInfo}
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddCategoryDialog(false);
              setNewTagName('');
              setSelectedTagIcon(null);
              setCreateTagPrivate(false);
            }}
          >
            {copy.cancel}
          </Button>
          <Button
            onClick={handleAddTag}
            variant="contained"
            disabled={!newTagName.trim()}
          >
            {copy.suggest}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Icon Selection Dialog */}
      <Dialog
        open={showIconDialog}
        onClose={() => setShowIconDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {copy.iconDialogTitle}
          <IconButton
            onClick={() => setShowIconDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* No Icon Option */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<LabelIcon />}
              onClick={() => handleSelectIcon(null)}
              sx={{ mb: 1 }}
            >
              {copy.noIcon}
            </Button>
          </Box>

          {/* Icons grouped by category */}
          {Object.entries(iconCategories).map(([category, icons]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {icons.map((iconOpt) => {
                  const IconComp = iconOpt.icon;
                  return (
                    <Tooltip key={iconOpt.name} title={iconOpt.label}>
                      <Paper
                        elevation={selectedTagIcon === iconOpt.name ? 3 : 1}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                          minWidth: 80,
                          border: selectedTagIcon === iconOpt.name ? '2px solid' : '1px solid',
                          borderColor: selectedTagIcon === iconOpt.name ? 'primary.main' : 'divider',
                          backgroundColor: selectedTagIcon === iconOpt.name ? 'primary.lighter' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                        onClick={() => handleSelectIcon(iconOpt.name)}
                      >
                        <IconComp />
                        <Typography variant="caption" textAlign="center">
                          {iconOpt.label}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* Context Menu for Publishing Private Categories */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handlePublishPrivateCategory}>
          <ListItemIcon>
            <PublicIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{copy.publish}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {copy.deleteTitle}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {copy.deletePrompt}
            </Typography>
          </Alert>
          {categoryToDelete && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {categoryToDelete.data?.icon && React.createElement(
                  getIconComponent(categoryToDelete.data.icon),
                  { fontSize: 'small' }
                )}
                <Typography variant="body2" fontWeight="bold">
                  {categoryToDelete.name}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                {copy.createdByLabel.replace('{{user}}', categoryToDelete.data?.createdBy || copy.unknown)}
              </Typography>
              {categoryToDelete.data?.isPrivate && (
                <Typography variant="caption" display="block" color="textSecondary">
                  {copy.private}
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            {copy.cannotUndo}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            {copy.cancel}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineIcon />}
          >
            {copy.deleteCategory}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesManagement;
